import { cx, omit, useAppVariant } from "@jugl-web/utils";
import React, { ReactNode, forwardRef } from "react";
import { LoadingSpinner } from "../LoadingSpinner";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "tertiary"
  | "tertiaryNoBg"
  | "danger"
  | "dangerNoBg";

export type ButtonSize = "lg" | "md" | "sm";

type NativeButtonProps = React.DetailedHTMLProps<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
>;

export interface ButtonProps extends Omit<NativeButtonProps, "disabled"> {
  children?: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  isDisabled?: boolean;
  isLoading?: boolean;
  fullWidth?: boolean;
  iconStart?: ReactNode;
  iconEnd?: ReactNode;
  isGeneral?: boolean;
  isCx?: boolean;
}

const hasGradientBorder = (variant: ButtonVariant) =>
  variant === "primary" || variant === "secondary";

const GRADIENT_STYLE = {
  background: "linear-gradient(118.33deg, #A9BDFF 4.21%, #F586FF 100.83%)",
};

// CX mode classes
const variantToContentClasses: Record<ButtonVariant, string> = {
  primary: cx(
    "bg-[#1A2023] text-white fill-white",
    "group-hover/btn:bg-black",
    "group-disabled/btn:group-hover/btn:bg-[#1A2023]"
  ),
  secondary: cx(
    "bg-white text-[#1A2023] fill-[#1A2023]",
    "outline outline-1 outline-white group-hover/btn:outline-0",
    "group-disabled/btn:group-hover/btn:bg-white group-disabled/btn:group-hover/btn:outline-1"
  ),
  tertiary: cx(
    "bg-[#F9F9F9] text-[#40494D] fill-[#40494D] hover:bg-[#F2F2F4]",
    "group-hover/btn:bg-[#F2F2F4]",
    "group-disabled/btn:text-[#A7AEB0] group-disabled/btn:fill-[#A7AEB0] group-disabled/btn:group-hover/btn:bg-[#F9F9F9]"
  ),
  tertiaryNoBg: cx(
    "bg-transparent text-[#1A2023] fill-[#1A2023]",
    "group-hover/btn:[text-decoration:underline]",
    "group-disabled/btn:[text-decoration:underline]"
  ),
  danger: cx(
    "bg-[#D75144] text-white fill-white",
    "group-hover/btn:bg-[#C94841]",
    "group-disabled/btn:bg-[#EA5353] group-disabled/btn:group-hover/btn:bg-[#EA5353]"
  ),
  dangerNoBg: cx(
    "bg-transparent text-[#D75144] fill-[#D75144]",
    "group-hover/btn:[text-decoration:underline]",
    "group-disabled/btn:[text-decoration:underline]"
  ),
};

// General mode overrides for primary/secondary (no gradient, uses primary color)
const variantToGeneralClasses: Partial<Record<ButtonVariant, string>> = {
  primary: cx(
    "bg-primary text-white fill-white",
    "hover:bg-primary-800",
    "disabled:hover:bg-primary"
  ),
  secondary: cx(
    "bg-transparent border border-solid border-primary text-primary fill-primary",
    "hover:border-primary-800 hover:text-primary-800 hover:fill-primary-800",
    "disabled:hover:border-primary disabled:hover:text-primary disabled:hover:fill-primary"
  ),
};

// For non-gradient variants: applied directly to <button>
const sizeToClasses: Record<ButtonSize, string> = {
  lg: "h-[48px] px-6 rounded-[12px]",
  md: "h-[40px] px-6 rounded-[12px]",
  sm: "h-[32px] px-4 rounded-[10px]",
};

// For gradient variants: height + outer radius on <button>
const sizeToOuterGradientClasses: Record<ButtonSize, string> = {
  lg: "h-[48px] rounded-[12px]",
  md: "h-[40px] rounded-[12px]",
  sm: "h-[32px] rounded-[10px]",
};

// For gradient variants: inner content radius = outer radius - border width
// Primary border = 1px, Secondary border = 2px
const sizeToInnerClasses: Record<ButtonVariant, Record<ButtonSize, string>> = {
  primary: {
    lg: "px-6 rounded-[11px]",
    md: "px-6 rounded-[11px]",
    sm: "px-4 rounded-[9px]",
  },
  secondary: {
    lg: "px-6 rounded-[10px]",
    md: "px-6 rounded-[10px]",
    sm: "px-4 rounded-[8px]",
  },
  tertiary: { lg: "", md: "", sm: "" },
  tertiaryNoBg: { lg: "", md: "", sm: "" },
  danger: { lg: "", md: "", sm: "" },
  dangerNoBg: { lg: "", md: "", sm: "" },
};

const spinnerColorMap: Record<ButtonVariant, "white" | "dark"> = {
  primary: "white",
  secondary: "dark",
  tertiary: "dark",
  tertiaryNoBg: "dark",
  danger: "white",
  dangerNoBg: "dark",
};

const generalSpinnerColorMap: Partial<
  Record<ButtonVariant, "white" | "primary">
> = {
  primary: "white",
  secondary: "primary",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant = "primary",
      size = "md",
      isDisabled,
      isLoading,
      fullWidth,
      iconStart,
      iconEnd,
      isGeneral: isGeneralProp,
      className,
      isCx: isCxProp,
      ...props
    },
    ref
  ) => {
    const { isGeneral: isGeneralEnv } = useAppVariant();
    const isGeneral = (isGeneralProp ?? isGeneralEnv) && !isCxProp;
    const disabled = isDisabled || isLoading;
    const showGradient = hasGradientBorder(variant) && !isGeneral;

    const contentClasses =
      isGeneral && variantToGeneralClasses[variant]
        ? variantToGeneralClasses[variant]
        : variantToContentClasses[variant];

    const spinnerColor =
      isGeneral && generalSpinnerColorMap[variant]
        ? generalSpinnerColorMap[variant]
        : spinnerColorMap[variant];

    return (
      <button
        ref={ref}
        data-variant={variant}
        className={cx(
          "group/btn relative inline-flex shrink-0 cursor-pointer items-center justify-center border-none bg-transparent p-0 select-none",
          "disabled:cursor-not-allowed disabled:opacity-40",
          fullWidth && "w-full shrink",
          showGradient &&
            cx(
              variant === "secondary" ? "p-[2px]" : "p-px",
              sizeToOuterGradientClasses[size]
            ),
          !showGradient && sizeToClasses[size],
          !showGradient && contentClasses,
          !showGradient &&
            "gap-2.5 font-secondary text-sm leading-[1.4] font-medium capitalize",
          className
        )}
        disabled={disabled}
        type="button"
        {...omit(props as NativeButtonProps, "disabled")}
      >
        {showGradient && (
          <div
            className="absolute inset-0 rounded-[inherit]"
            style={GRADIENT_STYLE}
          />
        )}
        {showGradient ? (
          <div
            className={cx(
              "relative flex h-full w-full items-center justify-center gap-2.5",
              "font-secondary text-sm leading-[1.4] font-medium capitalize",
              contentClasses,
              sizeToInnerClasses[variant][size]
            )}
          >
            {iconStart}
            {children && <span className="whitespace-nowrap">{children}</span>}
            {isLoading ? (
              <LoadingSpinner size="xs" color={spinnerColor} />
            ) : (
              iconEnd
            )}
          </div>
        ) : (
          <>
            {iconStart}
            {children && <span className="whitespace-nowrap">{children}</span>}
            {isLoading ? (
              <LoadingSpinner size="xs" color={spinnerColor} />
            ) : (
              iconEnd
            )}
          </>
        )}
      </button>
    );
  }
);

export default Button;
